import Button from "antd/lib/button/button";
import Descriptions from "antd/lib/descriptions";
import React, { RefObject } from "react";
import { Fragment } from "react";
import {
  FormFieldConfig,
  MembersPortalAccount,
  SelfServiceActivity,
  UssdAccount,
  UssdAccountStatusEnum,
  UssdAccountStatusTypeEnum,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  getStateColor,
  injectKey,
} from "../helpers/utilities";
import { Card, Divider, FormInstance, Input, Spin, Table, Tag } from "antd";
import moment from "moment";
import { UpdateUssdAccount } from "../pages/Ussd";

export const UssdAccountUI = (props: {
  ussdAccount: UssdAccount;
  changeUssdAccountStatus: (id: string, accountStatus: number) => Promise<void>;
  resetUssdAccountPin: (id: string) => Promise<void>;
  isLoading?: boolean;
  inlineDetails?: boolean;
  buttonsPosition?: "top" | "bottom";
  extraActions?: React.ReactNode;
}): JSX.Element => {
  const inactive =
    props.ussdAccount.accountStatus === UssdAccountStatusTypeEnum.inactive
      ? true
      : false;
  const actionButtons = (
    <>
      {props.ussdAccount &&
        (props.ussdAccount.accountStatus ===
        UssdAccountStatusTypeEnum.active ? (
          <>
            <Button
              id="block-account-button"
              className="error-btn"
              style={{ marginRight: "5px" }}
              onClick={(): void => {
                props.changeUssdAccountStatus(
                  props.ussdAccount.id,
                  UssdAccountStatusEnum.blocked,
                );
              }}
            >
              Block Account
            </Button>
            <Button
              id="deactivate-account-button"
              className="error-btn"
              style={{ marginRight: "5px" }}
              onClick={(): void => {
                props.changeUssdAccountStatus(
                  props.ussdAccount.id,
                  UssdAccountStatusEnum.inactive,
                );
              }}
            >
              Deactivate Account
            </Button>
          </>
        ) : props.ussdAccount.accountStatus ===
            UssdAccountStatusTypeEnum.blocked ||
          props.ussdAccount.accountStatus ===
            UssdAccountStatusTypeEnum.inactive ? (
          <Button
            id="enable-account-button"
            className="success-btn"
            style={{ marginRight: "5px" }}
            onClick={(): void => {
              props.changeUssdAccountStatus(
                props.ussdAccount.id,
                UssdAccountStatusEnum.active,
              );
            }}
          >
            Enable Account
          </Button>
        ) : null)}

      {!inactive && (
        <Button
          id="reset-account-button"
          className="warning-btn"
          style={{ marginRight: "5px" }}
          disabled={
            props.ussdAccount?.accountStatus ===
            UssdAccountStatusTypeEnum.active
              ? false
              : true
          }
          onClick={(): void => {
            props.resetUssdAccountPin(props.ussdAccount.id);
          }}
        >
          Reset Pin
        </Button>
      )}

      {props.extraActions}
    </>
  );

  return (
    <Spin spinning={props.isLoading}>
      {props.buttonsPosition === "top" && (
        <div
          style={{ display: "flex", flexWrap: "wrap", marginBottom: "16px" }}
        >
          {actionButtons}
        </div>
      )}

      {props.inlineDetails ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
          <div>
            <b>USSD Status</b>:{" "}
            {inactive ? (
              <Tag color={getStateColor("Not Registered")}>Not Registered</Tag>
            ) : (
              <Tag
                color={getStateColor(props.ussdAccount.accountStatus)}
                style={{ textTransform: "capitalize" }}
              >
                {formatSnakeCase(props.ussdAccount.accountStatus)}
              </Tag>
            )}
          </div>
          <div>
            <b>USSD Mobile Number</b>:{" "}
            {inactive ? "" : props.ussdAccount.phone.number}
          </div>
        </div>
      ) : (
        <>
          <Descriptions>
            <Descriptions.Item label={<b>USSD Status</b>}>
              {inactive ? (
                <Tag color={getStateColor("Not Registered")}>
                  Not Registered
                </Tag>
              ) : (
                <Tag
                  color={getStateColor(props.ussdAccount.accountStatus)}
                  style={{ textTransform: "capitalize" }}
                >
                  {formatSnakeCase(props.ussdAccount.accountStatus)}
                </Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions>
            <Descriptions.Item label={<b>USSD Mobile Number</b>}>
              {inactive ? "" : props.ussdAccount.phone.number}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}

      {props.buttonsPosition !== "top" && actionButtons}
    </Spin>
  );
};

export const selfServiceActivityColumns = [
  {
    title: "Channel",
    dataIndex: "channel",
    key: "channel",
    width: "10%",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{capitalizeFirstLetter(text)}</span>;
    },
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    width: "40%",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Date",
    dataIndex: "updatedAt",
    key: "date",
    width: "20%",
    render(
      text: string,
      selfServiceActivity: SelfServiceActivity,
    ): JSX.Element {
      return (
        <span>
          {selfServiceActivity.updatedAt
            ? moment(
                selfServiceActivity.updatedAt,
                "YYYY-MM-DD HH:mm:ss",
              ).format("YYYY-MM-DD HH:mm")
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "Sub-Category",
    dataIndex: "subCategory",
    key: "subCategory",
    width: "25%",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
];

export const EmptyUssdAccount = (props: {
  inlineDetails?: boolean;
}): JSX.Element => {
  return (
    <Fragment>
      {props.inlineDetails ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
          <div>
            <b>USSD Status</b>: {"Not Registered"}
          </div>
          <div>
            <b>USSD Mobile Number</b>: {""}
          </div>
        </div>
      ) : (
        <>
          <Descriptions>
            <Descriptions.Item label={<b>USSD Status</b>}>
              {"Not Registered"}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions>
            <Descriptions.Item label={<b>USSD Mobile Number</b>}>
              {""}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Fragment>
  );
};

export const SelfServiceActivityUI = (props: {
  ussdAccount?: UssdAccount;
  portalAccount?: MembersPortalAccount;
  activities: SelfServiceActivity[];
  filterActivity: (e: React.ChangeEvent<HTMLInputElement>) => void;
  changeUssdAccountStatus: (id: string, accountStatus: number) => Promise<void>;
  resetUssdAccountPin: (id: string) => Promise<void>;
  isLoading: boolean;
  reference: RefObject<FormInstance>;
  changeNumberFormConfig: FormFieldConfig[];
  showConfirmCreateModal: boolean;
  showConfirmUpdateModal: boolean;
  updateUssdAccountPhone: () => Promise<void>;
  toggleModal: () => void;
  toggleShowConfirmCreateModal: () => void;
  toggleShowConfirmUpdateModal: () => void;
}): JSX.Element => {
  return (
    <Spin spinning={props.isLoading}>
      <Fragment>
        <Card>
          <div>
            {props.ussdAccount &&
            props.ussdAccount.hasOwnProperty("accountStatus") ? (
              <>
                <UssdAccountUI
                  ussdAccount={props.ussdAccount}
                  changeUssdAccountStatus={props.changeUssdAccountStatus}
                  resetUssdAccountPin={props.resetUssdAccountPin}
                  isLoading={false}
                />
                <br />
                <UpdateUssdAccount
                  reference={props.reference}
                  changeNumberFormConfig={props.changeNumberFormConfig}
                  showConfirmUpdateModal={props.showConfirmUpdateModal}
                  isLoading={props.isLoading}
                  updateUssdAccountPhone={props.updateUssdAccountPhone}
                  toggleShowConfirmUpdateModal={
                    props.toggleShowConfirmUpdateModal
                  }
                />
              </>
            ) : (
              <EmptyUssdAccount />
            )}

            <Divider />

            <Descriptions>
              <Descriptions.Item label={<b>Members Portal Status</b>}>
                {props.portalAccount
                  ? capitalizeFirstLetter(
                      props.portalAccount.registrationStatus,
                    )
                  : "Not Registered"}
              </Descriptions.Item>
            </Descriptions>
            <Descriptions>
              <Descriptions.Item label={<b>Members Portal Username</b>}>
                {props.portalAccount ? props.portalAccount.username : ""}
              </Descriptions.Item>
            </Descriptions>
          </div>
        </Card>

        <Divider />

        <div
          style={{
            marginBottom: "20px",
          }}
        >
          <h1>Customer Activities</h1>
          <Input.Search
            size="middle"
            type="text"
            placeholder="Filter by Channel, Description, Date or Sub-Category"
            onChange={props.filterActivity}
          />
        </div>

        <Table
          style={{ width: "100%" }}
          bordered={true}
          columns={selfServiceActivityColumns}
          dataSource={injectKey<SelfServiceActivity>(props.activities, "id")}
          pagination={false}
        />
      </Fragment>
    </Spin>
  );
};
